import type { TrackingIntegration } from '@chukua/common';
import { Button, Popconfirm, Tabs, Tag } from 'antd';
import { FileX, Plus, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { DetailPageSkeleton } from '../../../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { PageHeader } from '../../../../../shared/components/PageHeader';
import { withinSection } from '../../../../../shared/components/section-trail';
import { INTEGRATION_KIND_LABELS, integrationTabPath } from '../../../integrations.paths';
import { DeviceMappingSheet } from '../components/DeviceMappingSheet';
import { DeviceMappingsTable } from '../components/DeviceMappingsTable';
import { GpsIntegrationSettings } from '../components/GpsIntegrationSettings';
import { UnmappedDevicesPanel } from '../components/UnmappedDevicesPanel';
import { GPS_INTEGRATION_TAB_LABELS, GPS_INTEGRATION_TABS, gpsIntegrationTabPath, mapDevicePath, type GpsIntegrationTab } from '../gps.paths';
import { useGpsIntegration } from '../hooks/use-gps-integration';
import { lastTestSummary } from '../policies/integration-status.policy';

interface GpsIntegrationPageProps {
  tab: GpsIntegrationTab;
  /** The map sheet's route opens it over the Devices tab. */
  sheet?: 'map';
}

const GPS_TRAIL = withinSection('integrations', { label: INTEGRATION_KIND_LABELS.gps, to: integrationTabPath('gps') });

/** One GPS account: its devices and which truck each is on, and its settings. */
export function GpsIntegrationPage({ tab, sheet }: GpsIntegrationPageProps) {
  const { id = '' } = useParams();
  const [searchParams] = useSearchParams();
  const gps = useGpsIntegration(id);
  const { state } = gps;

  if (state.status === 'loading') return <DetailPageSkeleton label="Loading the GPS integration" breadcrumbs={GPS_TRAIL} tabs={GPS_INTEGRATION_TABS.length} />;
  if (state.status === 'not_found')
    return <EmptyState icon={FileX} title="GPS integration not found" description="It may have been uninstalled, or the link is out of date." />;
  if (state.status === 'error')
    return <EmptyState icon={ShieldAlert} title="Couldn't load this GPS integration" description="Something went wrong. Try reloading the page." />;

  if (state.integration.status === 'uninstalled') return <Navigate to={integrationTabPath('gps')} replace />;
  return <GpsIntegrationView integration={state.integration} gps={gps} tab={tab} sheet={sheet} deviceId={searchParams.get('device') ?? undefined} />;
}

interface GpsIntegrationViewProps extends GpsIntegrationPageProps {
  integration: TrackingIntegration;
  gps: ReturnType<typeof useGpsIntegration>;
  deviceId: string | undefined;
}

function GpsIntegrationView({ integration, gps, tab, sheet, deviceId }: GpsIntegrationViewProps) {
  const navigate = useNavigate();
  // Bumped after a device is mapped, so the unmapped list and the table read again.
  const [devicesVersion, setDevicesVersion] = useState(0);
  const closeSheet = () => navigate(gpsIntegrationTabPath(integration.id, 'devices'));

  return (
    <>
      <PageHeader
        breadcrumbs={GPS_TRAIL}
        title={integration.displayName}
        description={`${integration.adapterName} · ${lastTestSummary(integration)}`}
        actions={<HeaderActions integration={integration} tab={tab} onEnable={gps.enable} onDisable={gps.disable} />}
      />
      <Tabs
        activeKey={tab}
        onChange={(key) => navigate(gpsIntegrationTabPath(integration.id, key as GpsIntegrationTab))}
        items={[
          {
            key: 'devices',
            label: GPS_INTEGRATION_TAB_LABELS.devices,
            children: (
              <div key={devicesVersion} className="flex flex-col gap-5">
                <UnmappedDevicesPanel integrationId={integration.id} />
                <DeviceMappingsTable integrationId={integration.id} />
              </div>
            ),
          },
          {
            key: 'settings',
            label: GPS_INTEGRATION_TAB_LABELS.settings,
            children: (
              <GpsIntegrationSettings
                integration={integration}
                onSaved={gps.replace}
                onUninstall={async () => {
                  await gps.uninstall(integration);
                  navigate(integrationTabPath('gps'));
                }}
              />
            ),
          },
        ]}
      />
      {sheet === 'map' && (
        <DeviceMappingSheet
          integrationId={integration.id}
          deviceId={deviceId}
          onClose={closeSheet}
          onMapped={() => {
            setDevicesVersion((version) => version + 1);
            closeSheet();
          }}
        />
      )}
    </>
  );
}

interface HeaderActionsProps {
  integration: TrackingIntegration;
  tab: GpsIntegrationTab;
  onEnable: (integration: TrackingIntegration) => Promise<void>;
  onDisable: (integration: TrackingIntegration) => Promise<void>;
}

function HeaderActions({ integration, tab, onEnable, onDisable }: HeaderActionsProps) {
  const navigate = useNavigate();
  const isOn = integration.status === 'enabled';
  return (
    <div className="flex flex-wrap items-center gap-2">
      {isOn ? <Tag color="success">On</Tag> : <Tag>Off</Tag>}
      {isOn ? (
        <Popconfirm
          title={`Disable ${integration.displayName}?`}
          description="No new positions arrive from it until it is enabled again."
          okText="Disable"
          onConfirm={() => onDisable(integration)}
        >
          <Button>Disable</Button>
        </Popconfirm>
      ) : (
        <Button onClick={() => onEnable(integration)}>Enable</Button>
      )}
      {tab === 'devices' && (
        <Button type="primary" icon={<Plus className="size-4" />} onClick={() => navigate(mapDevicePath(integration.id))}>
          Map a device
        </Button>
      )}
    </div>
  );
}
